import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { sortBy } from 'lodash';
import { mapMenuTabKeys } from 'client/utils/constants/appStates';

import LegendCategory from 'client/pages/MapPage/components/Legend/LegendCategory/LegendCategory';
import { getDisplayableLegendItemsForCategory } from 'client/pages/MapPage/components/Legend/Legend.utils';

import styles from './Legend.module.scss';

const Legend = ({ sectorSpecificStyles, dynamicLegend }) => {
    const hierarchy = useSelector(state => state?.layers?.result?.hierarchy);
    const mapLayers = useSelector(state => state?.mapConfig?.result?.layers);
    const mapStyles = useSelector(state => state?.mapConfig?.result?.styles);
    const currentLocale = useSelector(state => state?.locale?.current);
    const selectedOwner = useSelector(state => state?.map?.ownerId);
    const selectedTab = useSelector(state => state?.map?.menuSelectedTab);
    const showFiltered = selectedTab === mapMenuTabKeys.ISOLATED_VIEWS && selectedOwner;
    // Keep legend data selection aligned with AssetLayers so the dynamic legend
    // does not stay stuck on isolated-view assets after switching tabs.
    const assets = useSelector(state => showFiltered ? state?.assets?.filteredLayers || {} : state?.assets?.layers || {});
    const toggledLayers = useSelector(state => state.layers.toggledLayers);

    const visibleCategories = sortBy(hierarchy?.children || [], node => node.data.displayOrder)
        .map(categoryNode => ({
            categoryNode,
            legendItems: getDisplayableLegendItemsForCategory({
                categoryNode,
                currentLocale,
                mapLayers,
                assets,
                toggledLayers,
                dynamicLegend,
                sectorSpecificStyles,
                mapStyles
            })
        }))
        .filter(({ legendItems }) => legendItems.length > 0);

    const hasEntries = visibleCategories.length > 0;

    return (
        <div className={styles.container} data-testid="mapPage.menu.legend">
            {hasEntries
                ? visibleCategories.map(({ categoryNode, legendItems }, idx) => (
                    <LegendCategory
                        key={categoryNode.data.name[currentLocale]}
                        categoryNode={categoryNode}
                        currentLocale={currentLocale}
                        legendItems={legendItems}
                        isLast={idx === visibleCategories.length - 1}
                    />
                ))
                : <FormattedMessage id={'legend.noAssets'} />}
        </div>
    );
};

Legend.propTypes = {
    dynamicLegend: PropTypes.bool,
    sectorSpecificStyles: PropTypes.shape({
        enabled: PropTypes.bool,
        sector: PropTypes.string
    })
};

Legend.defaultProps = {
    dynamicLegend: false,
    sectorSpecificStyles: {
        enabled: false,
        sector: null
    }
};

export default Legend;
